import { observer } from 'mobx-react-lite'
import { NumericInputContainer } from '@/components/inputs/NumericInputContainer'
import { SelectContainer } from '@/components/inputs/SelectContainer'
import { CheckboxInputContainer } from '@/components/inputs/CheckboxInputContainer'
import { US_STATES } from '@/assets/data/us-states-territories'
import { type EstimatedIrsPaymentDialogViewModel } from './EstimatedIrsPaymentDialogViewModel'
import { type StateEstimatedTaxPaymentEntryViewModel } from './StateEstimatedTaxPaymentEntryViewModel'
import {
  EstimatedIrsPaymentDialog,
  EstimatedIrsPaymentStateEntryRow,
} from '@/features/jobs/details/components/JobProgressionCard/EstimatedIrsPaymentDialog'

export interface EstimatedIrsPaymentDialogContainerProps {
  vm: EstimatedIrsPaymentDialogViewModel
}

export const EstimatedIrsPaymentDialogContainer = observer(
  function EstimatedIrsPaymentDialogContainer({
    vm,
  }: EstimatedIrsPaymentDialogContainerProps) {
    return (
      <EstimatedIrsPaymentDialog
        showing={vm.modal.showing}
        insufficientDocuments={vm.insufficientDocuments}
        insufficientDocumentsInput={
          <CheckboxInputContainer
            model={vm}
            field="insufficientDocuments"
            label="Not enough documents received to calculate extension payment"
            testId="estimated-tax-payment-insufficient-documents"
          />
        }
        federalAmountInput={
          <NumericInputContainer
            model={vm}
            precision={2}
            field="federalAmount"
            name="federalAmount"
            label="Estimated federal tax payment"
            testId="estimated-tax-payment-federal-amount"
          />
        }
        isSubmitPending={vm.submit.pending}
        isSubmitDisabled={
          vm.submit.pending ||
          (!vm.insufficientDocuments && vm.hasIncompleteStateEntries)
        }
        onClose={vm.modal.close}
        onAddState={vm.addStateEntry}
        onSubmit={vm.submit}
      >
        {vm.stateEntries.map((entry, index) => (
          <StateEntryRowContainer
            key={entry.id}
            vm={entry}
            index={index}
            onRemove={() => vm.removeStateEntry(index)}
          />
        ))}
      </EstimatedIrsPaymentDialog>
    )
  },
)

interface StateEntryRowContainerProps {
  vm: StateEstimatedTaxPaymentEntryViewModel
  index: number
  onRemove: () => void
}

const StateEntryRowContainer = observer(function StateEntryRowContainer({
  vm,
  index,
  onRemove,
}: StateEntryRowContainerProps) {
  return (
    <EstimatedIrsPaymentStateEntryRow
      index={index}
      onRemove={onRemove}
      stateInput={
        <SelectContainer
          model={vm}
          field="state"
          items={US_STATES}
          label="State"
          placeholder="Select a state"
          data-test={`estimated-tax-payment-state-name-${index}`}
        />
      }
      amountInput={
        <NumericInputContainer
          model={vm}
          precision={2}
          field="amount"
          name={`state-amount-${index}`}
          label="Amount"
          testId={`estimated-tax-payment-state-amount-${index}`}
        />
      }
    />
  )
})
